import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ExamChatVoice from '../components/ExamChatVoice';
import {
  Loader2, Camera, ShieldCheck, Clock, AlertTriangle, CheckCircle2,
  Send, Video,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const fmtTimer = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// Prüfungsraum — Vollbild-Prüfung mit Kamera-Aufsicht
export default function ExamRoom() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  const [attempt, setAttempt] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | intro | running | done | error
  const [consent, setConsent] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const answersRef = useRef({});
  const dirtyRef = useRef(false);
  const autoSubmitRef = useRef(false);
  const phaseRef = useRef('loading');
  phaseRef.current = phase;

  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const logEvent = useCallback((type, detail = '') => {
    axios.post(`${API}/user/exam-attempts/${attemptId}/event`, { type, detail }, authRef.current()).catch(() => {});
  }, [attemptId]);

  // Attempt laden
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/user/exam-attempts/${attemptId}`, authRef.current());
        const a = r.data.attempt;
        setAttempt(a);
        if (a.status === 'freigeschaltet') setPhase('intro');
        else if (a.status === 'laufend') {
          setAnswers(a.answers || {});
          answersRef.current = a.answers || {};
          setRemaining(a.remaining_seconds || 0);
          setPhase('running');
        } else setPhase('done');
      } catch (e) {
        setPhase('error');
      }
    })();
  }, [attemptId]);

  // Kamera starten (Intro + Running)
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 480 }, height: { ideal: 360 }, frameRate: { ideal: 12, max: 15 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraReady(true);
      return true;
    } catch {
      setCameraReady(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if ((phase === 'intro' || phase === 'running') && attempt?.camera_required && !streamRef.current) {
      startCamera();
    }
    return () => {};
  }, [phase, attempt, startCamera]);

  // Kamera-Stream aufräumen
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // Video-Element nach Phase-Wechsel neu verbinden
  useEffect(() => {
    if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current;
  }, [phase]);

  // ===== Laufende Prüfung: Timer, Autosave, Frames, Ereignisse =====
  useEffect(() => {
    if (phase !== 'running') return undefined;

    const timer = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          if (!autoSubmitRef.current) { autoSubmitRef.current = true; setTimeout(() => doSubmit(true), 0); }
          return 0;
        }
        return r - 1;
      });
    }, 1000);

    const saver = setInterval(async () => {
      if (!dirtyRef.current) return;
      try {
        const r = await axios.post(`${API}/user/exam-attempts/${attemptId}/autosave`,
          { answers: answersRef.current }, authRef.current());
        if (r.data.saved) {
          dirtyRef.current = false;
          setLastSaved(new Date());
          if (typeof r.data.remaining_seconds === 'number') setRemaining(r.data.remaining_seconds);
        } else if (r.data.status && r.data.status !== 'laufend') {
          setPhase('done');
        }
      } catch { /* Netzwerk-Hänger: nächster Tick versucht erneut */ }
    }, 10000);

    // Kamera-Frames (~3s) an den Live-Monitor
    let framer = null;
    if (attempt?.camera_required) {
      const canvas = document.createElement('canvas');
      canvas.width = 480; canvas.height = 360;
      framer = setInterval(() => {
        const v = videoRef.current;
        if (!v || !streamRef.current || v.readyState < 2) return;
        try {
          canvas.getContext('2d').drawImage(v, 0, 0, 480, 360);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.45);
          axios.post(`${API}/user/exam-attempts/${attemptId}/frame`, { image: dataUrl }, authRef.current()).catch(() => {});
        } catch { /* noop */ }
      }, 3000);

      // Kamera-Abbruch erkennen
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (track) track.onended = () => { setCameraReady(false); logEvent('camera_off', 'Stream beendet'); };
    }

    const onVisibility = () => {
      logEvent(document.hidden ? 'tab_hidden' : 'tab_visible');
    };
    const onFullscreen = () => {
      if (!document.fullscreenElement) logEvent('fullscreen_exit');
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('fullscreenchange', onFullscreen);

    return () => {
      clearInterval(timer);
      clearInterval(saver);
      if (framer) clearInterval(framer);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', onFullscreen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, attemptId]);

  const start = async () => {
    if (attempt.camera_required && !cameraReady) {
      const ok = await startCamera();
      if (!ok) { toast.error('Ohne Kamera kann die Prüfung nicht starten. Bitte Kamerazugriff erlauben.'); return; }
    }
    if (attempt.camera_required && !consent) { toast.error('Bitte stimme der Kamera-Aufsicht zu.'); return; }
    try {
      const r = await axios.post(`${API}/user/exam-attempts/${attemptId}/start`, { consent }, authRef.current());
      const a = r.data.attempt;
      setAttempt(a);
      setAnswers(a.answers || {});
      answersRef.current = a.answers || {};
      setRemaining(a.remaining_seconds || 0);
      setPhase('running');
      try { await document.documentElement.requestFullscreen(); logEvent('fullscreen_enter'); } catch { /* optional */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  const setAnswer = (qid, val) => {
    const next = { ...answersRef.current, [qid]: val };
    answersRef.current = next;
    dirtyRef.current = true;
    setAnswers(next);
  };

  const doSubmit = async (auto = false) => {
    if (phaseRef.current !== 'running') return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/exam-attempts/${attemptId}/submit`, { answers: answersRef.current }, authRef.current());
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch { /* noop */ } }
      setAttempt((a) => ({ ...a, status: 'abgegeben', auto_submitted: auto }));
      setPhase('done');
      toast.success(auto ? 'Zeit abgelaufen — Prüfung automatisch abgegeben' : 'Prüfung abgegeben');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Abgabe fehlgeschlagen');
    }
    setSubmitting(false);
    setConfirmSubmit(false);
  };

  // ================= RENDER =================
  if (phase === 'loading') {
    return <div className="min-h-screen bg-cream-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-terra" /></div>;
  }
  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-cream-50 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <AlertTriangle className="w-10 h-10 text-terra" />
        <p className="text-sand-700">Prüfung nicht gefunden oder kein Zugriff.</p>
        <button onClick={() => navigate('/dashboard')} className="px-6 py-2.5 rounded-full bg-terra text-cream-50 text-[12px] font-bold uppercase tracking-widest" data-testid="exam-back-dashboard">Zum Dashboard</button>
      </div>
    );
  }

  // ---------- INTRO / EINWILLIGUNG ----------
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#2A1613] text-cream-50 flex items-center justify-center px-4 py-10">
        <div className="max-w-2xl w-full" data-testid="exam-intro">
          <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.24em] text-[#D89232] mb-3">Prüfungsraum · Dickies-Campus</p>
          <h1 className="font-display text-3xl sm:text-4xl mb-2">{attempt.exam_title}</h1>
          <p className="text-cream-50/70 text-[14px] mb-8">{attempt.course_title} · Zeitlimit {attempt.duration_minutes} Minuten · {attempt.questions_per_attempt} Fragen · 1 Versuch</p>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-5 space-y-2.5 text-[13.5px] text-cream-50/85">
            <p className="font-semibold text-cream-50">So läuft die Prüfung ab:</p>
            <p>· Der Timer startet mit dem Klick auf „Prüfung starten" und läuft durchgehend.</p>
            <p>· Deine Antworten werden alle 10 Sekunden automatisch gespeichert.</p>
            <p>· Einfügen per Copy & Paste ist gesperrt. Tab-Wechsel werden protokolliert.</p>
            <p>· Nach Ablauf der Zeit wird automatisch abgegeben.</p>
            <p>· Nach der Abgabe kannst du deine Prüfung als PDF herunterladen.</p>
          </div>

          {attempt.camera_required ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-5">
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-4 h-4 text-[#D89232]" />
                <p className="font-semibold text-[14px]">Kamera-Aufsicht (Pflicht)</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-56 aspect-[4/3] bg-black/40 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
                  {cameraReady
                    ? <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="exam-camera-preview" />
                    : (
                      <button onClick={startCamera} className="flex flex-col items-center gap-2 text-cream-50/60 text-[12px]" data-testid="exam-enable-camera">
                        <Video className="w-6 h-6" /> Kamera aktivieren
                      </button>
                    )}
                </div>
                <div className="text-[12.5px] text-cream-50/70 leading-relaxed">
                  <p className="mb-2">Während der Prüfung überträgt deine Webcam Bilder an die Prüfungsaufsicht und es werden regelmäßig Fotos gespeichert (Identitäts- und Täuschungskontrolle).</p>
                  <p>Die Aufnahmen werden ausschließlich zur Prüfungskontrolle verwendet und nach der Bewertung gelöscht (DSGVO Art. 6 Abs. 1 lit. b).</p>
                </div>
              </div>
              <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#E07A5F]" data-testid="exam-consent-checkbox" />
                <span className="text-[13px] text-cream-50/85">Ich stimme der Kamera-Aufsicht und der Speicherung der Aufnahmen zur Prüfungskontrolle zu.</span>
              </label>
            </div>
          ) : (
            <div className="bg-white/5 border border-[#D89232]/40 rounded-2xl p-5 mb-5 text-[13px] text-cream-50/85" data-testid="exam-no-camera-note">
              <p className="font-semibold text-[#D89232] mb-1">Kamera-Ausnahme durch die Akademie</p>
              <p>Für dich wurde die Kamera-Pflicht aufgehoben. Bitte filme dich während der Prüfung parallel mit deinem Handy und reiche das Video nach Absprache ein.</p>
            </div>
          )}

          <button
            onClick={start}
            disabled={attempt.camera_required && (!cameraReady || !consent)}
            className="w-full sm:w-auto px-10 py-4 rounded-full bg-[#E07A5F] text-cream-50 text-[13px] font-bold uppercase tracking-[0.16em] disabled:opacity-40 transition-opacity"
            data-testid="exam-start-btn"
          >
            Prüfung jetzt starten
          </button>
        </div>
      </div>
    );
  }

  // ---------- FERTIG / ERGEBNIS ----------
  if (phase === 'done') {
    const res = attempt?.result;
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center" data-testid="exam-done">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: res?.outcome === 'nicht_bestanden' ? '#A83232' : '#3E7A4E' }} />
          <h1 className="font-display text-3xl text-espresso mb-2">
            {res ? (res.outcome === 'bestanden' ? 'Bestanden!' : 'Leider nicht bestanden') : 'Prüfung abgegeben'}
          </h1>
          <p className="text-sand-600 text-[14px] mb-2">
            {res ? 'Deine Prüfung wurde bewertet.' : 'Deine Antworten sind sicher gespeichert. Die Akademie prüft deine Prüfung und meldet sich mit dem Ergebnis.'}
          </p>
          {attempt?.auto_submitted && <p className="text-[12.5px] text-terra mb-2">Hinweis: Die Zeit war abgelaufen — automatische Abgabe.</p>}
          {res?.percent !== undefined && res?.percent !== null && (
            <p className="text-[14px] font-semibold text-espresso mb-2" data-testid="exam-result-points">
              {res.total} von {res.total_max} Punkten ({res.percent} %)
            </p>
          )}
          {res?.feedback && <p className="text-[13px] italic text-sand-700 mb-4" data-testid="exam-result-feedback">Feedback: {res.feedback}</p>}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <button onClick={() => navigate('/dashboard')} className="px-6 py-3 rounded-full bg-espresso text-cream-50 text-[12px] font-bold uppercase tracking-widest" data-testid="exam-done-dashboard">
              Zum Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- LAUFENDE PRÜFUNG ----------
  const low = remaining <= 300;
  return (
    <div className="min-h-screen bg-cream-50" data-testid="exam-running">
      {/* Sticky Prüfungs-Leiste */}
      <div className="sticky top-0 z-40 bg-[#2A1613] text-cream-50 px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D89232]">Prüfung läuft</p>
          <p className="text-[13px] font-semibold truncate">{attempt.exam_title}</p>
        </div>
        <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
          {attempt.camera_required && (
            <div className="hidden sm:block w-20 h-[60px] rounded-lg overflow-hidden bg-black/50 relative">
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
              <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${cameraReady ? 'bg-green-400' : 'bg-red-500 animate-pulse'}`} />
            </div>
          )}
          <div className="text-right">
            <p className={`font-mono text-xl font-bold ${low ? 'text-red-400' : 'text-cream-50'}`} data-testid="exam-timer">
              <Clock className="w-4 h-4 inline mr-1.5 -mt-1" />{fmtTimer(remaining)}
            </p>
            <p className="text-[10px] text-cream-50/50">
              {lastSaved ? `Gespeichert ${lastSaved.toLocaleTimeString('de-DE')}` : 'Autosave aktiv'}
            </p>
          </div>
          <button
            onClick={() => setConfirmSubmit(true)}
            className="px-5 py-2.5 rounded-full bg-[#E07A5F] text-cream-50 text-[11px] font-bold uppercase tracking-widest"
            data-testid="exam-submit-btn"
          >
            <Send className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Abgeben
          </button>
        </div>
      </div>

      {!cameraReady && attempt.camera_required && (
        <div className="bg-red-50 border-b border-red-200 text-red-800 text-[13px] px-4 sm:px-8 py-2.5 flex items-center gap-2" data-testid="exam-camera-warning">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Kamera unterbrochen — bitte sofort wieder aktivieren.
          <button onClick={startCamera} className="underline font-semibold">Kamera aktivieren</button>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24">
        <p className="text-[13px] text-sand-600 mb-6 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-terra" />
          {attempt.questions?.length} Fragen · {attempt.total_points || ''} Punkte gesamt · Multiple Choice ankreuzen, offene Fragen in eigenen Worten beantworten.
        </p>
        {(attempt.questions || []).map((q, i) => (
          <div key={q.id} className="mb-8 bg-cream-100 border border-cream-300 rounded-2xl p-5" data-testid={`exam-question-${i + 1}`}>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.2em]" style={{ color: '#8F650F' }}>
                Frage {i + 1} von {attempt.questions.length} · {q.type === 'mc' ? 'Multiple Choice' : q.type === 'mc_multi' ? 'Multiple Choice — mehrere Antworten möglich' : 'Offene Frage'}
              </p>
              <span className="text-[11px] font-bold text-sand-600 whitespace-nowrap" data-testid={`exam-points-${i + 1}`}>{q.points} {q.points === 1 ? 'Punkt' : 'Punkte'}</span>
            </div>
            <p className="text-[15px] text-espresso font-semibold leading-relaxed mb-4">{q.text}</p>
            {(q.type === 'mc' || q.type === 'mc_multi') ? (
              <div className="space-y-2" data-testid={`exam-mc-options-${i + 1}`}>
                {q.type === 'mc_multi' && (
                  <p className="text-[11.5px] font-semibold text-terra">Mehrere Antworten möglich — bitte alle zutreffenden ankreuzen.</p>
                )}
                {(q.options || []).map((opt, oi) => {
                  const multiSel = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                  const isSel = q.type === 'mc_multi' ? multiSel.includes(oi) : String(answers[q.id]) === String(oi);
                  return (
                  <label
                    key={oi}
                    className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${isSel ? 'border-terra bg-terra/5' : 'border-cream-300 bg-white hover:border-sand-400'}`}
                  >
                    <input
                      type={q.type === 'mc_multi' ? 'checkbox' : 'radio'}
                      name={`q-${q.id}`}
                      checked={isSel}
                      onChange={() => {
                        if (q.type === 'mc_multi') {
                          const next = isSel ? multiSel.filter((x) => x !== oi) : [...multiSel, oi].sort((a, b) => a - b);
                          setAnswer(q.id, next);
                        } else setAnswer(q.id, oi);
                      }}
                      className="mt-0.5 accent-[#E07A5F]"
                      data-testid={`exam-mc-${i + 1}-${oi}`}
                    />
                    <span className="text-[14px] text-sand-800 leading-snug">
                      <span className="font-semibold mr-1.5">{String.fromCharCode(65 + oi)})</span>{opt}
                    </span>
                  </label>
                  );
                })}
              </div>
            ) : (
              <>
                <textarea
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  onPaste={(e) => { e.preventDefault(); logEvent('paste_blocked', `Frage ${i + 1}`); toast.error('Einfügen ist in der Prüfung nicht erlaubt'); }}
                  rows={6}
                  placeholder="Deine Antwort…"
                  className="w-full px-4 py-3 bg-white border border-cream-300 rounded-xl text-[14px] text-sand-800 leading-relaxed focus:outline-none focus:border-terra resize-y"
                  data-testid={`exam-answer-${i + 1}`}
                />
                <p className="text-[11px] text-sand-500 mt-1.5 text-right">{(answers[q.id] || '').trim() ? `${String(answers[q.id]).trim().split(/\s+/).length} Wörter` : ''}</p>
              </>
            )}
          </div>
        ))}
        <button
          onClick={() => setConfirmSubmit(true)}
          className="w-full py-4 rounded-full bg-[#E07A5F] text-cream-50 text-[13px] font-bold uppercase tracking-[0.16em]"
          data-testid="exam-submit-bottom"
        >
          Prüfung abgeben
        </button>
      </div>

      {/* Chat + Video-/Sprachverbindung zur Aufsicht */}
      <ExamChatVoice attemptId={attemptId} getAuthHeader={getAuthHeader} role="user" getCameraStream={() => streamRef.current} />

      {confirmSubmit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="bg-cream-50 rounded-2xl p-6 max-w-md w-full" data-testid="exam-confirm-modal">
            <h3 className="font-display text-xl text-espresso mb-2">Prüfung wirklich abgeben?</h3>
            <p className="text-[13.5px] text-sand-600 mb-1">
              Beantwortet: {(attempt.questions || []).filter((q) => answers[q.id] !== undefined && (Array.isArray(answers[q.id]) ? answers[q.id].length > 0 : String(answers[q.id]).trim() !== '')).length} von {(attempt.questions || []).length} Fragen.
            </p>
            <p className="text-[13px] text-sand-600 mb-5">Nach der Abgabe sind keine Änderungen mehr möglich.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmSubmit(false)} className="px-5 py-2.5 rounded-full border border-cream-400 text-sand-700 text-[11px] font-bold uppercase tracking-widest" data-testid="exam-confirm-cancel">Weiter schreiben</button>
              <button onClick={() => doSubmit(false)} disabled={submitting} className="px-5 py-2.5 rounded-full bg-[#E07A5F] text-cream-50 text-[11px] font-bold uppercase tracking-widest disabled:opacity-50" data-testid="exam-confirm-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Endgültig abgeben'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
